/**
 * Hook para obtener datos de Tracking usando React Query
 * Hook to get Tracking data using React Query
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { TrackingApiAdapter } from '../adapters';
import {
  FilesComparisonResponse,
  WebhooksComparisonResponse,
  ApiComparisonResponse,
  CourierStatusResponse,
  OutdatedOrdersResponse,
  HistorySummaryResponse,
  HistoryFailedResponse,
  RetryHistoryRequest,
  RetryHistoryResponse,
  UnmatchedRecordsResponse,
} from '../models/TrackingApiResponse';
import { TrackingFilters, PaginationParams } from '../models/TrackingFilters';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface UseTrackingDataProps {
  filters?: TrackingFilters;
  pagination?: PaginationParams;
}

export const useTrackingData = ({ filters, pagination }: UseTrackingDataProps = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const trackingAdapter = new TrackingApiAdapter(httpClient);
  const { toast } = useToast();
  const { t } = useTranslation('trackingModule');
  const queryClient = useQueryClient();

  // Estabilizar filtros para evitar cambios de referencia innecesarios
  const stableFilters = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) return {};
    return filters;
  }, [JSON.stringify(filters)]);

  // Estabilizar paginación
  const stablePagination = useMemo(() => {
    return pagination || { per_page: 50 };
  }, [pagination?.per_page]);

  // Dashboard - Comparativas
  const filesComparison = useQuery<FilesComparisonResponse>({
    queryKey: ['tracking', 'filesComparison', stableFilters],
    queryFn: () => trackingAdapter.getFilesComparison(stableFilters),
    staleTime: 1 * 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    enabled: true,
  });

  const webhooksComparison = useQuery<WebhooksComparisonResponse>({
    queryKey: ['tracking', 'webhooksComparison', stableFilters],
    queryFn: () => trackingAdapter.getWebhooksComparison(stableFilters),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: true,
  });

  const apiComparison = useQuery<ApiComparisonResponse>({
    queryKey: ['tracking', 'apiComparison', stableFilters],
    queryFn: () => trackingAdapter.getApiComparison(stableFilters),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: true,
  });

  // API Calls - Estado de Couriers
  const courierStatus = useQuery<CourierStatusResponse>({
    queryKey: ['tracking', 'courierStatus'],
    queryFn: () => trackingAdapter.getCourierStatus(),
    staleTime: 30 * 1000, // 30 segundos (datos más dinámicos)
    gcTime: 2 * 60 * 1000,
    refetchInterval: 60 * 1000, // Refrescar cada minuto
  });

  // API Calls - Pedidos Desfasados (con scroll infinito)
  const outdatedOrders = useInfiniteQuery<OutdatedOrdersResponse>({
    queryKey: ['tracking', 'outdatedOrders', JSON.stringify(stableFilters)],
    queryFn: ({ pageParam = 1 }) =>
      trackingAdapter.getOutdatedOrders(stableFilters, { page: pageParam, per_page: stablePagination.per_page || 50 }),
    getNextPageParam: (lastPage) => {
      if (lastPage && lastPage.current_page < lastPage.last_page) {
        return lastPage.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    placeholderData: (previousData) => {
      // Solo mantener datos previos si existen páginas con datos válidos
      if (previousData && previousData.pages && previousData.pages.length > 0) {
        const hasValidData = previousData.pages.some(
          (page) => page?.data && Array.isArray(page.data) && page.data.length > 0
        );
        return hasValidData ? previousData : undefined;
      }
      return undefined;
    },
  });

  // Histórico - Resumen
  const historySummary = useQuery<HistorySummaryResponse>({
    queryKey: ['tracking', 'historySummary', stableFilters],
    queryFn: () => trackingAdapter.getHistorySummary(stableFilters),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Histórico - Fallidos (con scroll infinito)
  const historyFailed = useInfiniteQuery<HistoryFailedResponse>({
    queryKey: ['tracking', 'historyFailed', JSON.stringify(stableFilters)],
    queryFn: ({ pageParam = 1 }) =>
      trackingAdapter.getHistoryFailed(stableFilters, { page: pageParam, per_page: stablePagination.per_page || 50 }),
    getNextPageParam: (lastPage) => {
      if (lastPage && lastPage.current_page < lastPage.last_page) {
        return lastPage.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => {
      // Solo mantener datos previos si existen páginas con datos válidos
      if (previousData && previousData.pages && previousData.pages.length > 0) {
        const hasValidData = previousData.pages.some(
          (page) => page?.data && Array.isArray(page.data) && page.data.length > 0
        );
        return hasValidData ? previousData : undefined;
      }
      return undefined;
    },
  });

  // Unmatched Records (con scroll infinito)
  const unmatchedRecords = useInfiniteQuery<UnmatchedRecordsResponse>({
    queryKey: ['tracking', 'unmatchedRecords', JSON.stringify(stableFilters)],
    queryFn: ({ pageParam = 1 }) =>
      trackingAdapter.getUnmatchedRecords(stableFilters, { page: pageParam, per_page: stablePagination.per_page || 50 }),
    getNextPageParam: (lastPage) => {
      if (lastPage && lastPage.current_page < lastPage.last_page) {
        return lastPage.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => {
      // Solo mantener datos previos si existen páginas con datos válidos
      if (previousData && previousData.pages && previousData.pages.length > 0) {
        const hasValidData = previousData.pages.some(
          (page) => page?.data && Array.isArray(page.data) && page.data.length > 0
        );
        return hasValidData ? previousData : undefined;
      }
      return undefined;
    },
  });

  // Mutación para reintentar histórico
  const retryHistoryMutation = useMutation<
    RetryHistoryResponse,
    Error,
    RetryHistoryRequest
  >({
    mutationFn: (request) => trackingAdapter.retryHistory(request),
    onSuccess: () => {
      toast({
        title: t('toasts.success.title'),
        description: t('toasts.success.retry_initiated'),
        variant: 'default',
      });
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['tracking', 'historyFailed'] });
      queryClient.invalidateQueries({ queryKey: ['tracking', 'historySummary'] });
    },
    onError: (error) => {
      toast({
        title: t('toasts.error.title'),
        description: error.message || t('toasts.error.unexpected'),
        variant: 'destructive',
      });
    },
  });

  return {
    // Dashboard
    filesComparison: {
      data: filesComparison.data,
      isLoading: filesComparison.isLoading,
      isFetching: filesComparison.isFetching,
      error: filesComparison.error,
    },
    webhooksComparison: {
      data: webhooksComparison.data,
      isLoading: webhooksComparison.isLoading,
      isFetching: webhooksComparison.isFetching,
      error: webhooksComparison.error,
    },
    apiComparison: {
      data: apiComparison.data,
      isLoading: apiComparison.isLoading,
      isFetching: apiComparison.isFetching,
      error: apiComparison.error,
    },
    // API Calls
    courierStatus: {
      data: courierStatus.data,
      isLoading: courierStatus.isLoading,
      isFetching: courierStatus.isFetching,
      error: courierStatus.error,
    },
    outdatedOrders: {
      data: outdatedOrders.data?.pages.flatMap((page) => page.data) ?? [],
      total: outdatedOrders.data?.pages[0]?.total ?? 0,
      isLoading: outdatedOrders.isLoading,
      isFetching: outdatedOrders.isFetching,
      isFetchingNextPage: outdatedOrders.isFetchingNextPage,
      error: outdatedOrders.error,
      fetchNextPage: outdatedOrders.fetchNextPage,
      hasNextPage: outdatedOrders.hasNextPage ?? false,
    },
    // Histórico
    historySummary: {
      data: historySummary.data,
      isLoading: historySummary.isLoading,
      isFetching: historySummary.isFetching,
      error: historySummary.error,
    },
    historyFailed: {
      data: historyFailed.data?.pages.flatMap((page) => page.data) ?? [],
      total: historyFailed.data?.pages[0]?.total ?? 0,
      isLoading: historyFailed.isLoading,
      isFetching: historyFailed.isFetching,
      isFetchingNextPage: historyFailed.isFetchingNextPage,
      error: historyFailed.error,
      fetchNextPage: historyFailed.fetchNextPage,
      hasNextPage: historyFailed.hasNextPage ?? false,
    },
    retryHistory: retryHistoryMutation.mutateAsync,
    isRetryingHistory: retryHistoryMutation.isPending,
    // Unmatched
    unmatchedRecords: {
      data: unmatchedRecords.data?.pages.flatMap((page) => page.data) ?? [],
      total: unmatchedRecords.data?.pages[0]?.total ?? 0,
      isLoading: unmatchedRecords.isLoading,
      isFetching: unmatchedRecords.isFetching,
      isFetchingNextPage: unmatchedRecords.isFetchingNextPage,
      error: unmatchedRecords.error,
      fetchNextPage: unmatchedRecords.fetchNextPage,
      hasNextPage: unmatchedRecords.hasNextPage ?? false,
    },
  };
};

